// "Occupation des salles" button shown in the header of the planning pages.
// Renders nothing without the ROOM_OCCUPANCY_VIEW permission; opens the
// overlay (lazy-loaded) on the campus and date the page is looking at.
import React, { Suspense, lazy, useState } from 'react';
import { Building2 } from 'lucide-react';
import { isSameWeek } from 'date-fns';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import type { CampusInfo } from '@/services/groupsService';
import type { PlanningContext } from './planHere.utils';
import { isPlanHereEnabled } from './featureFlags';

const RoomOccupancyOverlay = lazy(() =>
  import('./RoomOccupancyOverlay').then((m) => ({ default: m.RoomOccupancyOverlay })),
);

interface RoomOccupancyButtonProps {
  campuses: CampusInfo[];
  campusId: string | null;
  date: Date;
  academicYearId?: string;
  /** Allows "Planifier ici" from free slots; only honoured with SCHEDULE_MANAGE. */
  planning?: PlanningContext | null;
}

export const RoomOccupancyButton: React.FC<RoomOccupancyButtonProps> = ({ campuses, campusId, date, academicYearId, planning }) => {
  const { hasPermission } = usePermissions();
  const [open, setOpen] = useState(false);

  if (!hasPermission(Permission.ROOM_OCCUPANCY_VIEW)) return null;

  // Open on today when the page shows the current week, else on the week shown
  const initialDate = isSameWeek(date, new Date(), { weekStartsOn: 1 }) ? new Date() : date;
  // Phase 2 is gated: staging/local only until validated (see featureFlags.ts)
  const canPlan = !!planning && isPlanHereEnabled() && hasPermission(Permission.SCHEDULE_MANAGE);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
        title="Voir les salles par étage et les séances qui les occupent"
      >
        <Building2 className="h-4 w-4" />
        Occupation des salles
      </button>
      {open && (
        <Suspense fallback={null}>
          <RoomOccupancyOverlay
            campuses={campuses}
            initialCampusId={campusId}
            initialDate={initialDate}
            academicYearId={academicYearId}
            planning={canPlan && planning ? planning : undefined}
            onClose={() => setOpen(false)}
          />
        </Suspense>
      )}
    </>
  );
};
